import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete, {
	createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { useTheme } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ContactModal from "../ContactModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Typography } from '@material-ui/core';
import formatContactIdentifier, {
	isBsuidNumber,
} from "../../helpers/formatContactIdentifier";

const ForwardMessageModal = ({ messages, onClose, modalOpen }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
    const [optionsContacts, setOptionsContacts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [searchParam, setSearchParam] = useState("");
	const [selectedContact, setSelectedContact] = useState(null);
	const [newContact, setNewContact] = useState({});
	const [contactModalOpen, setContactModalOpen] = useState(false);
	const { user } = useContext(AuthContext);
	const [sending, setSending] = useState(false);
	const [messageSending, setMessageSending] = useState('');

    useEffect(() => {
		if (!modalOpen || searchParam.length < 3) {
			setLoading(false);
			return;
		}
		setLoading(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchContacts = async () => {
				try {
					const { data } = await api.get("contacts", {
						params: { searchParam },
					});
					setOptionsContacts(data.contacts);
					setLoading(false);
				} catch (err) {
					setLoading(false);
					toastError(err);
				}
			};

			fetchContacts();
		}, 500);
		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, modalOpen]);

	const history = useHistory();

	const sleep = (ms) => {
		return new Promise(resolve => setTimeout(resolve, ms));
	};

    const handleForwardMessage = async(contactL) => {
		// 2026-06-08 (Gal): track failures so we don't silently navigate
		// the user to /tickets while a toast quietly tells them the
		// forward failed. Old behaviour: every catch lost context and
		// the page jumped anyway, hiding the toast under the new screen.
		setSending(true);
		let hadError = false;
		for (const message of messages) {
			try {
				setMessageSending(message.id);
				await api.post('/message/forward', {
					messageId: message.id,
					contactId: contactL.id,
				});
				// Was: `sleep(900)` (no await) — the wait never happened,
				// so a multi-message forward fired all sends concurrently.
				// Now actually paced 900ms between sends to keep Cloud API
				// from rate-limiting Raviv's account.
				await sleep(900);
			} catch (error) {
				hadError = true;
				// Surface the backend's `detail` (the real Meta / Baileys
				// message) instead of just "Failed to forward message".
				const detail = error?.response?.data?.detail;
				if (detail) {
					// toastError reads .response.data.error by default; promote
					// detail into that slot so the user sees it.
					error.response.data.error = `${error.response.data.error}: ${detail}`;
				}
				toastError(error);
			}
		}
		setSending(false);
		// Only navigate on full success — otherwise leave the user on
		// the current ticket so they can read the toast and retry.
		if (!hadError) {
			history.push('/tickets');
		}
    }

    const handleSelectOption = (e, newValue) => {
		if (newValue?.number) {
			setSelectedContact(newValue);
		} else if (newValue?.name) {
			setNewContact({ name: newValue.name });
			setContactModalOpen(true);
		}
	};

    const handleClose = () => {
		onClose();
		setSearchParam("");
		setSelectedContact(null);
		setSending(false);
	};

    const handleCloseContactModal = () => {
		setContactModalOpen(false);
	};

    const renderOption = optionL => {
		if (optionL.number) {
			// BSUID (username-only) contacts store a synthetic wau_ key in
			// `number` — never show it; render the @username/friendly label.
			if (isBsuidNumber(optionL.number)) {
				return `${optionL.name} - ${formatContactIdentifier(optionL).display}`;
			}
			return `${optionL.name} - ${optionL.number}`;
		} else {
			return `${i18n.t("contactModal.noOption")} ${optionL.name}`;
		}
	};

	const renderOptionLabel = optionL => {
		if (optionL.number) {
			if (isBsuidNumber(optionL.number)) {
				return `${optionL.name} - ${formatContactIdentifier(optionL).display}`;
			}
			return `${optionL.name} - ${optionL.number}`;
		} else {
			return `${optionL.name}`;
		}
	};

	const filter = createFilterOptions({
		trim: true,
	});

	const createAddContactOption = (filterOptions, params) => {
		const filtered = filter(filterOptions, params);

		if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
			filtered.push({
				name: `${params.inputValue}`,
			});
		}

		return filtered;
	};

    return (
        <>
			<ContactModal
				open={contactModalOpen}
				initialValues={newContact}
				onClose={handleCloseContactModal}
				onSave={() => console.log('save')}
			></ContactModal>
			<Dialog open={modalOpen} onClose={handleClose} fullScreen={isMobile} maxWidth="sm" fullWidth>
				<DialogTitle id="form-dialog-title">
					{i18n.t("forwardMessageModal.dialog.title")}
				</DialogTitle>
				<DialogContent dividers>
					<Autocomplete
						options={optionsContacts}
						loading={loading}
						style={{ width: '100%', minWidth: isMobile ? 'auto' : 300 }}
						clearOnBlur
						autoHighlight
						freeSolo
						clearOnEscape
						getOptionLabel={renderOptionLabel}
						renderOption={renderOption}
						filterOptions={createAddContactOption}
						onChange={(e, newValue) => handleSelectOption(e, newValue)}
						renderInput={params => (
							<TextField
								{...params}
								label={i18n.t("newTicketModal.fieldLabel")}
								variant="outlined"
								autoFocus
								onChange={e => setSearchParam(e.target.value)}
								onKeyPress={e => {
									if (loading || !selectedContact) return;
									else if (e.key === "Enter") {
										// handleSaveTicket(selectedContact.id);
									}
								}}
								InputProps={{
									...params.InputProps,
									endAdornment: (
										<React.Fragment>
											{loading ? (
												<CircularProgress color="inherit" size={20} />
											) : null}
											{params.InputProps.endAdornment}
										</React.Fragment>
									),
								}}
							/>
						)}
					/>
				</DialogContent>
				<DialogActions>
					{sending && (
						<>
							<CircularProgress color="inherit" size={20} />
							<Typography variant="body1" color="textSecondary">
							{i18n.t("forwardMessageModal.dialog.sending")}...
							</Typography>
						</>
					)}
					<ButtonWithSpinner
						variant="contained"
						type="button"
						disabled={!selectedContact || sending}
						onClick={() => handleForwardMessage(selectedContact)}
						color="primary"
						loading={loading}
					>
					{i18n.t("forwardMessageModal.dialog.button")}
					</ButtonWithSpinner>
				</DialogActions>
			</Dialog>
		</>
    );
};

export default ForwardMessageModal;